/*
 * obligaciones.js — Lógica pura de las obligaciones adicionales (C-021, ver diseno/CAMBIOS.md).
 *
 * IMSS, impuesto sobre nómina (1.8%, trimestral) e impuesto sobre hospedaje. Cada una tiene
 * Envío al cliente → Pago, en cascada, y vence el día 17 del mes siguiente al que se declara
 * (la nómina, el 17 del mes siguiente al cierre del trimestre). La lista vive también en
 * backend/obligaciones.js.
 */
import { calcularDiasAtraso, fechaCorta, fechaLimiteDe } from './estados';

export const DIA_LIMITE = 17;

export const OBLIGACIONES = [
  { clave: 'imss', nombre: 'IMSS', corto: 'IMSS', trimestral: false },
  { clave: 'nomina', nombre: 'Impuesto sobre nómina (1.8%)', corto: 'Nómina (1.8%)', aviso: 'Nómina', trimestral: true },
  { clave: 'hospedaje', nombre: 'Impuesto sobre hospedaje', corto: 'Hospedaje', trimestral: false },
];

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const ORDINALES = ['1er', '2º', '3er', '4º'];

export const esCierreDeTrimestre = (mes) => mes % 3 === 0;

/** Trimestre de un mes (1-12): { numero, texto: '3er trimestre (jul–sep)', cierre } */
export const trimestreDe = (mes) => {
  const numero = Math.ceil(mes / 3);
  const cierre = numero * 3;
  return { numero, cierre, texto: `${ORDINALES[numero - 1]} trimestre (${CORTOS[cierre - 3]}–${CORTOS[cierre - 1]})` };
};

/** "septiembre" o "3er trimestre (jul–sep)". */
export const textoPeriodo = (obligacion, mes) =>
  (obligacion.trimestral ? trimestreDe(mes).texto : MESES[mes - 1]);

export const fechaLimiteObligacion = ({ mes, anio }) => fechaLimiteDe(DIA_LIMITE, { mes, anio });

/** Para la nómina en un mes que no cierra trimestre: "No toca en octubre. El siguiente es…" */
export const textoNoToca = ({ mes, anio }, hoy = new Date()) => {
  const t = trimestreDe(mes);
  const limite = fechaLimiteObligacion({ mes: t.cierre, anio });
  return `No toca en ${MESES[mes - 1]}. El siguiente es el ${t.texto}, a más tardar el ${fechaCorta(limite, hoy)}.`;
};

const hecho = (paso) => !!paso && (paso.estado === 'verde' || paso.estado === 'azul');

const claveDe = (paso, { actual, bloqueado }) => {
  if (hecho(paso)) return 'completado';
  if (bloqueado) return 'bloqueado';
  if (paso && paso.estado === 'amarillo') return 'en-proceso';
  return actual ? 'por-hacer' : 'bloqueado';
};

/**
 * Estado de un cliente en una obligación del mes:
 * { envio: clave visible, pago: clave visible, pagado, pasoActual: 'envio'|'pago'|null, atrasado, dias }
 * Las claves son las de NOMBRES_ESTADO (completado, en-proceso, por-hacer, bloqueado).
 */
export const estadoObligacion = (cliente, { mes, anio }, hoy = new Date()) => {
  const envioHecho = hecho(cliente.envio);
  const pagado = hecho(cliente.pago);
  const pasoActual = pagado ? null : envioHecho ? 'pago' : 'envio';
  const dias = pagado ? 0 : calcularDiasAtraso(DIA_LIMITE, { mes, anio }, hoy);
  return {
    envio: claveDe(cliente.envio, { actual: pasoActual === 'envio' }),
    pago: claveDe(cliente.pago, { actual: pasoActual === 'pago', bloqueado: !envioHecho && !pagado }),
    pagado,
    pasoActual,
    dias,
    atrasado: dias > 0,
  };
};

/** "1 de 6 pagados · 2 atrasados" */
export const textoAvance = (clientes, periodo, hoy = new Date()) => {
  const estados = clientes.map(c => estadoObligacion(c, periodo, hoy));
  const pagados = estados.filter(e => e.pagado).length;
  const atrasados = estados.filter(e => e.atrasado).length;
  return `${pagados} de ${clientes.length} ${clientes.length === 1 ? 'pagado' : 'pagados'}${atrasados ? ` · ${atrasados} ${atrasados === 1 ? 'atrasado' : 'atrasados'}` : ''}`;
};

/** Por cliente, las obligaciones atrasadas del mes: Map(clienteId → ['IMSS', 'Nómina', …]) */
export const atrasosPorCliente = (obligaciones, periodo, hoy = new Date()) => {
  const mapa = new Map();
  for (const o of obligaciones || []) {
    if (!o.toca) continue;
    const def = OBLIGACIONES.find(x => x.clave === o.clave);
    for (const c of o.clientes) {
      if (!estadoObligacion(c, periodo, hoy).atrasado) continue;
      mapa.set(c.id, [...(mapa.get(c.id) || []), (def && (def.aviso || def.corto)) || o.nombre]);
    }
  }
  return mapa;
};

/** Siguiente estado al hacer clic: Por hacer → En proceso → Completado → Por hacer. */
export const siguienteEstadoObligacion = (estado) =>
  (estado === 'amarillo' ? 'verde' : estado === 'verde' || estado === 'azul' ? 'rojo' : 'amarillo');
